import type {
  QuestionGroupProps,
  Answer,
} from "../../../../../../components/Questionnaire/Section/Question/types";
import { isQuestion } from "../../../../../../components/Questionnaire/Section/Question/types";
import type { VisibilityMap } from "../../../../../../components/Questionnaire/validation/form/types";
import type { Answers } from "../../../../types";
import type { FlattenGroupResult } from "../types";
import { getPrimaryAnswer, createEmptyFlattenResult } from "../utils";
import { SecondaryText } from "../utils/SecondaryText";

/**
 * consolidated_question_group
 * Includes the primary question and group questions with visibility and answers adjusted based on the primary question's answer.
 */
export const flattenYesNoToggleGroup = (
  group: Partial<QuestionGroupProps>,
  answers: Answers,
  visibility: VisibilityMap,
): FlattenGroupResult => {
  const { primaryQuestion, groupQuestions } = group;
  const filteredQuestions = groupQuestions?.filter(isQuestion) ?? [];

  // If the primary or group questions are missing, return an empty flatten group
  // Don't filter based on child visibility as they may be controlled by primary answer
  if (!primaryQuestion || !groupQuestions) {
    return createEmptyFlattenResult(answers, visibility);
  }

  const primaryAnswer = getPrimaryAnswer(primaryQuestion, answers);

  const answersByQuestionName = { ...answers };
  const visibilityByQuestionName = { ...visibility };
  const defaultNoAnswers: Record<string, Answer> = {};

  const questionsWithVisibility = filteredQuestions.map(q => {
    let answer: Answer | null = null;

    if (q.display?.type === "multiselect") {
      // [""] sentinel (no consolidatedDefault) or [consolidatedDefault] so Rails
      // receives name[]= and clears the field when primary is "no"
      const consolidatedDefault = (
        q.display?.config as Record<string, unknown> | undefined
      )?.consolidatedDefaultNoAnswer as string | undefined;
      defaultNoAnswers[q.name] = consolidatedDefault
        ? [consolidatedDefault]
        : [""];
    }

    // When primary is "no", use array sentinel for multiselect children, "no" for others
    if (primaryAnswer === "no") {
      answer = defaultNoAnswers[q.name] ?? "no";
    }

    // When primary is "yes", resolve child answers from q.answer (API/initial state).
    // Optional children with no answer stay null so they're excluded from the answers map.
    if (primaryAnswer === "yes") {
      if (q.display?.type === "multiselect") {
        const consolidatedDefault = (
          q.display?.config as Record<string, unknown> | undefined
        )?.consolidatedDefaultNoAnswer as string | undefined;
        // Strip "" and consolidatedDefaultNoAnswer sentinels left from a previous "no" session
        const sentinels = new Set<string>([""]);
        if (consolidatedDefault) sentinels.add(consolidatedDefault);
        if (Array.isArray(q.answer)) {
          answer = q.answer.filter(v => !sentinels.has(v));
        } else if (q.answer != null) {
          // Non-null non-array (e.g. stale string sentinel) — reset to []
          answer = [];
        }
        // null/undefined — leave answer as null (unanswered optional)
      } else {
        answer = q.answer || null;
      }
    }

    if (answer !== null) {
      answersByQuestionName[q.name] = answer;
    }

    visibilityByQuestionName[q.name] =
      primaryAnswer === "yes" ? !!q.visible : false;

    // Sync cleaned multiselect answer onto the question object so mergeState
    // (MERGE action) reads [] instead of the raw sentinel, keeping validation correct.
    const shouldSyncAnswer =
      answer !== null && q.display?.type === "multiselect";
    return {
      ...q,
      includeAnswer: !!q.visible,

      ...(shouldSyncAnswer && { answer: answer! }),
    };
  });

  // Update primary question's secondary text with the generated React element or preserve original
  const primaryQuestionWithSecondaryText = {
    ...primaryQuestion,
    secondaryText: SecondaryText({
      primaryQuestion,
      questions: filteredQuestions,
    }),
  };

  // Merge primary question and flattened group questions
  return {
    questions: [primaryQuestionWithSecondaryText, ...questionsWithVisibility],
    groups: {
      [primaryQuestion.name]: {
        kind: "group",
        names: questionsWithVisibility.map(q => q.name),
        defaultNoAnswers,
      },
    },
    visibility: visibilityByQuestionName,
    answers: {
      ...answersByQuestionName,
      [primaryQuestion.name]: primaryAnswer || "",
    },
  };
};
